import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Link to="/help" className="text-amber-700 hover:text-amber-800 font-medium mb-4 inline-block">
        ← Back to Help Center
      </Link>
      
      <h1 className="text-4xl font-bold mb-6 text-amber-800 dark:text-amber-200">Privacy Policy</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 dark:bg-gray-800 dark:text-gray-100 space-y-6">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
          </p>
        </div>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">1. Introduction</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            Welcome to QahwaHub. We are committed to protecting your privacy and ensuring you have a positive experience on our platform.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">2. Information We Collect</h2>
          <h3 className="text-xl font-semibold mb-2">Personal Information</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-3">
            <li>Name and email address</li>
            <li>Phone number (optional)</li>
            <li>Payment information (processed securely through approved payment providers - we never store full card details)</li>
            <li>QR code data (unique identifier for in-store payments)</li>
            <li>Subscription status and preferences</li>
          </ul>

          <h3 className="text-xl font-semibold mb-2">Usage Information</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>Order history and preferences (online and in-store)</li>
            <li>Location data (for route mapping and cafe recommendations - only with your permission)</li>
            <li>Device information and usage patterns</li>
            <li>Points balance, earning history, and redemption records</li>
            <li>Subscription status and payment history</li>
            <li>QR code scan events (when scanned in-store for payment processing)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">3. How We Use Your Information</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>To process and fulfill your orders</li>
            <li>To manage your account and rewards points</li>
            <li>To send you notifications about your orders and promotional offers</li>
            <li>To provide location-based services (cafe recommendations on your route)</li>
            <li>To improve our services and user experience</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">4. Information Sharing</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            We do not sell your personal information. We may share your information only in the following circumstances:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li><strong>With Cafes:</strong> We share order details with the cafe you're ordering from to fulfill your order. For in-store payments, we share your user ID and points information when your QR code is scanned.</li>
            <li><strong>Payment Processors:</strong> We share payment information with approved payment providers to process transactions. Providers are expected to be PCI DSS compliant and handle card data securely. We never receive or store your full card details.</li>
            <li><strong>Service Providers:</strong> We may share data with trusted third-party service providers who assist us in operating our app (analytics, cloud hosting, customer support)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights, safety, or the rights of our users</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">5. Data Security</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our security measures include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>Encryption of sensitive data in transit (HTTPS/TLS)</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>PCI DSS compliant payment processing through approved providers</li>
            <li>Regular security audits and updates</li>
            <li>Limited access to personal data on a need-to-know basis</li>
            <li>Secure database storage with access controls</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 mt-3">
            However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">6. Your Rights</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-3">You have the right to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>Access your personal information</li>
            <li>Update or correct your information through your profile settings</li>
            <li>
              Delete your account in the app under Profile or Settings, or on the web at{' '}
              <Link to="/delete-account" className="text-amber-700 underline">
                qahwahub.com/delete-account
              </Link>
              . You can also email support@qahwahub.com.
            </li>
            <li>Opt-out of promotional communications</li>
            <li>Request a copy of your data</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">7. Cookies and Tracking</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            We use local storage and session storage to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            <li>Remember your login status and authentication token</li>
            <li>Store your cart items temporarily</li>
            <li>Remember your preferences (dark mode, language settings)</li>
            <li>Maintain your session while using the app</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 mt-3">
            This data is stored locally on your device and is not shared with third parties. You can clear this data by clearing your browser/app cache or logging out.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">8. Children's Privacy</h2>
          <p className="text-gray-700 dark:text-gray-300">
            Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">9. Changes to This Policy</h2>
          <p className="text-gray-700 dark:text-gray-300">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-3 text-amber-700 dark:text-amber-300">10. Contact Us</h2>
          <p className="text-gray-700 dark:text-gray-300">
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <ul className="list-none space-y-1 text-gray-700 dark:text-gray-300 mt-2">
            <li>📧 Email: support@qahwahub.com</li>
            <li>📞 Phone: xxx-xxx-xxxx</li>
            <li>📍 Address: Amman, Jordan</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
